import axios from 'axios';

// API Configuration - Import from the correct location
const API_URL = 'https://b1.envicblue.co.uk/api';

const API_ENDPOINTS = {
  // Auth
  LOGIN: `${API_URL}/auth.php?action=login`,
  FORGOT_PASSWORD: `${API_URL}/auth.php?action=forgot-password`,
  RESET_PASSWORD: `${API_URL}/auth.php?action=reset-password`,
  CHANGE_PASSWORD: `${API_URL}/auth.php?action=change-password`,
  
  // Users
  USERS: `${API_URL}/users.php`,
  
  // Products
  PRODUCTS: `${API_URL}/products.php`,
  
  // Orders
  ORDERS: `${API_URL}/orders.php`,
  CREATE_ORDER: `${API_URL}/orders.php?action=create`,
  MY_ORDERS: `${API_URL}/orders.php?action=my-orders`,
  CURRENT_WEEK: `${API_URL}/orders.php?action=current-week`,
  
  // Weekly Orders
  WEEKLY_ORDERS: `${API_URL}/weekly_orders.php`,
  APPROVE_ORDERS: `${API_URL}/weekly_orders.php?action=approve`,
  PENDING_APPROVALS: `${API_URL}/weekly_orders.php?action=pending`,
  
  // Final Orders
  FINAL_ORDERS: `${API_URL}/final_orders.php`,
  PENDING_FINAL: `${API_URL}/final_orders.php?action=pending`,
  
  // Analytics
  DASHBOARD: `${API_URL}/analytics.php?action=dashboard`,
  TRENDS: `${API_URL}/analytics.php?action=trends`,
  PREDICTIONS: `${API_URL}/analytics.php?action=predictions`,
};

const STORAGE_KEYS = {
  TOKEN: 'ward_stock_token',
  USER: 'ward_stock_user',
};

// Create axios instance with default config
const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth Service
export const authService = {
  login: async (email, password) => {
    const response = await api.post(API_ENDPOINTS.LOGIN, { email, password });
    if (response.data.success) {
      localStorage.setItem(STORAGE_KEYS.TOKEN, response.data.data.token);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.data.data.user));
    }
    return response.data;
  },

  logout: () => {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem(STORAGE_KEYS.TOKEN);
  },

  forgotPassword: async (email) => {
    const response = await api.post(API_ENDPOINTS.FORGOT_PASSWORD, { email });
    return response.data;
  },

  resetPassword: async (token, password) => {
    const response = await api.post(API_ENDPOINTS.RESET_PASSWORD, { token, password });
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post(API_ENDPOINTS.CHANGE_PASSWORD, {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },
};

// Users Service
export const usersService = {
  getAll: async () => {
    const response = await api.get(API_ENDPOINTS.USERS);
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.USERS}?action=get&id=${id}`);
    return response.data;
  },

  create: async (userData) => {
    const response = await api.post(`${API_ENDPOINTS.USERS}?action=create`, userData);
    return response.data;
  },

  update: async (id, userData) => {
    const response = await api.put(`${API_ENDPOINTS.USERS}?action=update&id=${id}`, userData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.USERS}?action=delete&id=${id}`);
    return response.data;
  },

  toggleStatus: async (id, status) => {
    const response = await api.put(`${API_ENDPOINTS.USERS}?action=toggle-status&id=${id}`, { status });
    return response.data;
  },
};

// Products Service
export const productsService = {
  getAll: async () => {
    const response = await api.get(API_ENDPOINTS.PRODUCTS);
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.PRODUCTS}?action=get&id=${id}`);
    return response.data;
  },

  create: async (productData) => {
    const response = await api.post(`${API_ENDPOINTS.PRODUCTS}?action=create`, productData);
    return response.data;
  },

  update: async (id, productData) => {
    const response = await api.put(`${API_ENDPOINTS.PRODUCTS}?action=update&id=${id}`, productData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.PRODUCTS}?action=delete&id=${id}`);
    return response.data;
  },

  updateStock: async (id, quantity) => {
    const response = await api.put(`${API_ENDPOINTS.PRODUCTS}?action=update-stock&id=${id}`, { quantity });
    return response.data;
  },
};

// Orders Service
export const ordersService = {
  getAll: async () => {
    const response = await api.get(API_ENDPOINTS.ORDERS);
    return response.data;
  },

  getMyOrders: async () => {
    const response = await api.get(API_ENDPOINTS.MY_ORDERS);
    return response.data;
  },

  getCurrentWeek: async () => {
    const response = await api.get(API_ENDPOINTS.CURRENT_WEEK);
    return response.data;
  },

  create: async (orderData) => {
    const response = await api.post(API_ENDPOINTS.CREATE_ORDER, orderData);
    return response.data;
  },

  update: async (id, orderData) => {
    const response = await api.put(`${API_ENDPOINTS.ORDERS}?action=update&id=${id}`, orderData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.ORDERS}?action=delete&id=${id}`);
    return response.data;
  },
};

// Weekly Orders Service
export const weeklyOrdersService = {
  getAll: async () => {
    const response = await api.get(API_ENDPOINTS.WEEKLY_ORDERS);
    return response.data;
  },

  getPendingApprovals: async () => {
    const response = await api.get(API_ENDPOINTS.PENDING_APPROVALS);
    return response.data;
  },

  approve: async (weekId, approvalData) => {
    const response = await api.post(`${API_ENDPOINTS.APPROVE_ORDERS}&week_id=${weekId}`, approvalData);
    return response.data;
  },
};

// Final Orders Service
export const finalOrdersService = {
  getAll: async () => {
    const response = await api.get(API_ENDPOINTS.FINAL_ORDERS);
    return response.data;
  },

  getPending: async () => {
    const response = await api.get(API_ENDPOINTS.PENDING_FINAL);
    return response.data;
  },

  approve: async (orderId) => {
    const response = await api.post(`${API_ENDPOINTS.FINAL_ORDERS}?action=approve&id=${orderId}`);
    return response.data;
  },

  complete: async (orderId) => {
    const response = await api.post(`${API_ENDPOINTS.FINAL_ORDERS}?action=complete&id=${orderId}`);
    return response.data;
  },
};

// Analytics Service
export const analyticsService = {
  getDashboard: async () => {
    const response = await api.get(API_ENDPOINTS.DASHBOARD);
    return response.data;
  },

  getTrends: async (startDate, endDate) => {
    const response = await api.get(`${API_ENDPOINTS.TRENDS}&start_date=${startDate}&end_date=${endDate}`);
    return response.data;
  },

  getPredictions: async (productId) => {
    const response = await api.get(`${API_ENDPOINTS.PREDICTIONS}&product_id=${productId}`);
    return response.data;
  },
};

export default api;